import { EditorService, EditorServiceModels } from '@editor/client-rest-api';
import getUserLicenseType, { LicenseType } from '../utils/getUserLicenseType';
import { setDefaultOptionWithId } from '../actions/setDefaultOptionWithId';
import { setCategoryNameWithId } from '../actions/setCategoryNameWithId';
import { setDefaultOptionsEditor } from '../mutators/setDefaultOptionsEditor';
import { clearDefaultOptions } from '../actions/clearDefaultOptions';
import { logUsage } from 'owa-analytics';
import { getLogicalRing, getSessionId } from 'owa-config';
import { isDogfoodEnv } from 'owa-metatags';
import { isFeatureEnabled } from 'owa-feature-flags';

function getEditorServiceEndPoint(): string {
    return isDogfoodEnv()
        ? 'https://nleditor.osi.officeppe.net'
        : 'https://nleditor.osi.office.net';
}

async function doFetch(
    languageId: string,
    licenseTypePremiumForUi: boolean
): Promise<EditorServiceModels.CritiqueCategoryInfo[]> {
    const client = new EditorService({ baseUri: getEditorServiceEndPoint() });
    client.xUserSessionId = getSessionId();
    const editorTonalRewriteFeature = 'mc-officeEditorTonalEnabled';
    let descriptors = [
        {
            name: 'LicenseType',
            value: licenseTypePremiumForUi
                ? (LicenseType.Subscription as string)
                : (getUserLicenseType() as string),
        },
        {
            name: 'Audience',
            value: getAudienceGroup(),
        },
    ];

    isFeatureEnabled(editorTonalRewriteFeature) &&
        descriptors.push({
            name: 'FlightIds',
            value: editorTonalRewriteFeature,
        });

    const response = await client.configV2({
        appId: 'Outlook_Online',
        languageId: languageId,
        descriptors: descriptors,
    });

    if (response?.responseStatus === EditorServiceModels.ResponseStatus.Success) {
        return response.critiqueCategoryInfoList;
    } else {
        logUsage('EditorProofingCallFailed', { locale: languageId });
        return Promise.resolve([]);
    }
}

export default async function fetchDefaultConfigFromService(languageId: string): Promise<void> {
    // this a temporary call to get all premium suggestions to render in the settings UI
    // WI 59483 - remove this call once OWA integrate new shared settings

    const isLicenseTypeSubscription = getUserLicenseType() === LicenseType.Subscription;
    const responseForUI = await doFetch(languageId, true /* LicenseTypePremiumForUi */);
    clearDefaultOptions();
    responseForUI?.forEach(critiqueCategoryInfo => {
        const categoryName = critiqueCategoryInfo.name;
        const categoryId = critiqueCategoryInfo.id;
        const critiqueTypeInfoList = critiqueCategoryInfo.critiqueTypeInfoList;

        setCategoryNameWithId(categoryId, categoryName);
        critiqueTypeInfoList?.forEach(critiqueTypeInfo => {
            setDefaultOptionWithId(critiqueTypeInfo.id, categoryId, critiqueTypeInfo);
        });
        // for Subscription, the Editor and UI deafult options are the same
        if (isLicenseTypeSubscription) {
            critiqueTypeInfoList?.forEach(critiqueTypeInfo => {
                setDefaultOptionsEditor(critiqueTypeInfo.id, categoryId, critiqueTypeInfo);
            });
        }
    });

    // For non-Subscription users a second call is needed to get the correct options to pass down the Editor
    // to support the correct suggestions.
    if (!isLicenseTypeSubscription) {
        const responseForEditorProofing = await doFetch(
            languageId,
            false /* LicenseTypePremiumForUi */
        );
        responseForEditorProofing?.forEach(critiqueCategoryInfo => {
            const categoryId = critiqueCategoryInfo.id;
            const critiqueTypeInfoList = critiqueCategoryInfo.critiqueTypeInfoList;
            critiqueTypeInfoList?.forEach(critiqueTypeInfo => {
                setDefaultOptionsEditor(critiqueTypeInfo.id, categoryId, critiqueTypeInfo);
            });
        });
    }
}

export function getAudienceGroup(): string {
    switch (getLogicalRing()) {
        case 'Dogfood':
            return 'Dogfood';
        case 'Microsoft':
            return 'Microsoft';
        case 'FirstRelease':
        case 'WW':
        default:
            return 'Production';
    }
}
